<!-- Step 1 - Venue, event type and date -->
<script>
    import {onMount} from 'svelte';
    import { fade, blur, scale } from 'svelte/transition';
    import isLoading from '../stores/loading-store';
    import {reservation, steps} from '../stores/reservation-store';
    import FormNavigation from './event-form-navigation.svelte';
    import getAvailableDays from "../utils/get-available-days";
    import buildWeeks from "../../utils/booking-build-weeks";
    import debounce from 'lodash/debounce';

    import {
        i18n,
        i18nStore,
    } from "svelte-translate";
    const urlParams = new URLSearchParams(window.location.search);
    const maxPeople = 50; // #TODO: pull from json

    const getVenuesUrl = `/${$i18nStore.language}/api/venues/`;
    const feedCalendarUrl = `/${$i18nStore.language}/api/availability/`;

    let calendar = null,
    weeks = null,
    year = null,
    month = null,
    monthName = '',
    hasPreviousMonth = false,
    days = null,
    parks = null,
    apiParks = null;

    onMount(async () => {
        isLoading.set(true);
        let urlParkId = parseInt(urlParams.get('park'),10);
        const response = await fetch(getVenuesUrl);
        $reservation.eventType = "KOparty"
        $reservation.readableEventTypeName = "KoParty"
        $reservation.duration = 2;
        parks = await response.json();
        // parks = apiParks.filter(park => !park.no_events);
        $steps.nextStep = 2;
        if ($reservation.jumpDate && $reservation.numJumpers >= 6) {
            // date already selected, user has moved back in steps.
            $steps.nextStepAllowed = true;
            const jDate = new Date($reservation.jumpDate);
            month = jDate.getMonth()+1;
            year = jDate.getFullYear();
        } else {
            $steps.nextStepAllowed =false;
        }
        if (parks) {
            isLoading.set(false);
            if (urlParkId) {
                $reservation.parkId = parseInt(urlParams.get('park'),10);
                let selectedPark = (parks.filter(item => item.id === $reservation.parkId));
                $reservation.readableParkName = selectedPark[0].name;
                $reservation.maxJumpers = selectedPark[0].capacity;
            }
        }
        if (parks && $reservation.parkId && !$reservation.jumpDate) {
            await refreshCalendar();
        } else if (parks && $reservation.parkId && $reservation.jumpDate) {
            await refreshCalendar(null, null, true);
        }
    });

    const refreshCalendar = async (e, offset=null, direct=false) => {
        isLoading.set(true);
        let response = await getAvailableDays(feedCalendarUrl, $i18nStore.language, year, month, $reservation.parkId, $reservation.numJumpers, $reservation.duration, $reservation.eventType, offset, direct);
        calendar = response['calendar'];
        year = response['year'];
        month = response['month'];
        monthName = response['month_name'];
        hasPreviousMonth = response['has_previous_month'];
        $reservation.openingDate = response['opening_date'];
        $reservation.openingDateDisplay = response['opening_date_display'];
        days = response["days"];
        weeks = buildWeeks(calendar);
        isLoading.set(false);
    };

    const refreshAndResetCalendar = debounce(async (e) => {
        clearSelectedDate();
        if ($reservation.numJumpers < 6) {$reservation.numJumpers = 6};
        await refreshCalendar(e);
    }, 400);

    const changePark = async (e) => {
        let selectedPark = (parks.filter(item => item.id === $reservation.parkId));
        $reservation.readableParkName = selectedPark[0].name;
        $reservation.maxJumpers = selectedPark[0].capacity;
        if ($reservation.eventType) {
            clearSelectedDate();
            await refreshAndResetCalendar(e);
        }
    };

    const clearSelectedDate = () => {
        $reservation.jumpDate = null;
        $reservation.readableJumpDate = null;
        $reservation.timeSlot = null;
        $steps.nextStepAllowed = false;
        const selected = document.querySelector('.Calendar-day--selected');
        if(selected){
            selected.classList.remove('Calendar-day--selected');
        }
    };

    const selectDate = (e, cell) => {
        if (cell.open && !cell.passed && cell.fill_level !== 'full') {
            clearSelectedDate();
            $reservation.timeSlot = null; // also clear time slot in case this is a step back.
            $reservation.jumpDate = cell.date;
            $reservation.readableJumpDate = cell.readable_date;
            $steps.nextStep = 2;
            $reservation.timeSlot = 19;
            $steps.nextStepAllowed = true;
        }
    };

</script>


<div out:fade="{{delay: 0, duration: 150 }}" in:fade="{{delay: 500, duration: 200 }}" class="pb-4">
    <div class="event-notice">
        <p use:i18n={"headNotice1b"}>Book your Party below.</p>
        <p><span use:i18n={"headNotice2"}>If you want to book a standard jump session, head to the</span> <a href="{window.standardBookingUrl}" class="link-standard" use:i18n={"headNoticeLink"}>reservation form</a>.</p>
        <!--{#if window.siteIdentifier === "belgium"}-->
        <!--<div class="price-notice"><span use:i18n={"priceNotice"}>For Messancy, please contact us about Birthday/Party offers:</span>&nbsp;<a href="mailto:info@kojump.be" class="link-standard">info@kojump.be</a>.</div>-->
        <!--{/if}-->
        <!--{#if window.siteIdentifier === "morocco"}-->
        <!--    <div class="price-notice"><span use:i18n={"priceNoticeMa"}>For Casablanca, please contact us about Birthday/Party offers:</span>&nbsp;<a href="mailto:info@kojump.be" class="link-standard">info@kojump.be</a>.</div>-->
        <!--{/if}-->
    </div>
    <form class="Form-container">
        <div class="Form-split">

            <!-- VENUE SELECTION STEP -->
            <div class="Form-section">
                <div class="Form-label-wrapper">
                    <label for="park-select" class="Form-label" use:i18n={"parkLabel"}>Park</label>
                </div>
                <div class="Form-input-wrapper | relative">
                    {#if parks}
                        <select name="park" id="park-select" class="Form-select-input" bind:value="{$reservation.parkId}" on:change="{changePark}">
                            {#if !$reservation.parkId}
                                <option value="" use:i18n={"parkSelectMessage"}>-- Select a park --</option>
                            {/if}
                            {#each parks as park}
                                <option value="{park.id}">{park.name}</option>
                            {/each}
                        </select>
                    {:else}
                        <select name="country" id="pre-park-select" class="Form-select-input" value="na" disabled>
                            <option value="na" use:i18n={"parksLoadingMessage"}>Loading parks…</option>
                        </select>
                    {/if}
                    <div class="Form-input-symbol">
                        <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                    </div>
                </div>
            </div>


            <!-- PARTICIPANTS SELECTION STEP -->
            <div class="Form-section">
                <div class="Form-label-wrapper">
                    <label for="id_people" class="Form-label" use:i18n={"jumperCountLabel"}>Jumpers</label>
                </div>
                <div class="Form-input-wrapper relative">
                    <input type="number" id="id_people" min="6" max="{$reservation.maxJumpers}" class="" bind:value={$reservation.numJumpers} disabled="{!$reservation.parkId}" on:change="{refreshAndResetCalendar}">
                </div>
            </div>

            <!-- DATE DISPLAY -->
            <div class="Form-section">
                <div class="Form-label-wrapper">
                    <span class="Form-label" use:i18n={"dateLabel"}>Date: </span>
                </div>
                <div class="Form-input-wrapper">
                    {#if $reservation.jumpDate}<span class:just-changed="{$reservation.jumpDate}">{$reservation.readableJumpDate}</span>{:else if !$reservation.parkId}
                        <span class="Form-hint" use:i18n={"dateNote1"}>Select a park first.</span>{:else}
                        <span class="Form-hint" use:i18n={"dateNote3"}>Select a date from the calendar</span>{/if}
                </div>
            </div>

            {#if $reservation.jumpDate}
                <!-- TIME DISPLAY -->
                <div class="Form-section">
                    <div class="Form-label-wrapper">
                        <span class="Form-label" use:i18n={"timeLabel"}>Time: </span>
                    </div>
                    <div class="Form-input-wrapper">
                        <span class:just-changed="{$reservation.timeSlot}">19:00 - 21:00</span>
                    </div>
                </div>
            {/if}


</div>
        <!-- DATE SELECTION STEP -->
        <div class="Form-split--calendar">
        {#if weeks}
            <div transition:blur="{{delay: 250, duration: 300}}">
                {#if $reservation.openingDate}
                    <div class="Form-opening-notice">
                        <strong use:i18n={"openingDateTitle"}>Notice</strong>
                        <span class="" use:i18n={"openingDateNote"}>This park opens soon, bookings can only be made from that date.</span>
                    </div>
                {/if}
                <div class="Calendar-holder">
                    <div class="Calendar-header">
                        <div class="Calendar-header-nav--previous">
                            <button type="button"
                                    class="Calendar-header-nav-button Calendar-header-nav-button--previous"
                                    title="Previous month" disabled="{!hasPreviousMonth}"
                                    on:click="{(e) => refreshCalendar(e, 'previous')}">&laquo;
                            </button>
                        </div>
                        <div class="Calendar-header-month">{monthName} {year}</div>
                        <div class="Calendar-header-nav--next">
                            <button type="button"
                                    class="Calendar-header-nav-button Calendar-header-nav-button--next"
                                    title="next month" on:click="{(e) => refreshCalendar(e, 'next')}">&raquo;
                            </button>
                        </div>
                    </div>
                    <div class="Calendar-body w-full">
                        <table class="Calendar w-full collapse">
                            <thead>
                            <tr>
                                {#each days as day}
                                    <th scope="col" class="Calendar-dayname px-2 py-4">{day}</th>{/each}
                            </tr>
                            </thead>
                            <tbody>
                            {#each weeks as week}
                                <tr>
                                    {#each week as cell}
                                        <td class="Calendar-day bg-kj-black-l1 text-center p-1 cursor-pointer"
                                            class:Calendar-day--closed="{!cell.open}"
                                            class:Calendar-day--passed="{cell.passed || cell.unavailable}"
                                            class:Calendar-day--today="{cell.today}"
                                            class:Calendar-day--selected="{cell.date === $reservation.jumpDate}"
                                            class:Calendar-day--fill--low="{!cell.passed && !cell.unavailable && cell.open && cell.fill_level==='low'}"
                                            class:Calendar-day--fill--light="{!cell.passed && !cell.unavailable && cell.open && cell.fill_level==='light'}"
                                            class:Calendar-day--fill--medium="{!cell.passed && !cell.unavailable && cell.open && cell.fill_level==='medium'}"
                                            class:Calendar-day--fill--high="{!cell.passed && !cell.unavailable && cell.open && cell.fill_level==='high'}"
                                            class:Calendar-day--fill--full="{!cell.passed && !cell.unavailable && cell.open && cell.fill_level==='full'}"
                                            on:click|stopPropagation="{(e) => selectDate(e, cell)}"><span class="flex justify-center items-center border-b-4 border-transparent px-1 py-1 bg-kj-black-l3">{cell.day}</span>
                                        </td>
                                    {/each}
                                </tr>
                            {/each}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="Calendar-info">
                    <div class="Fill-legend">
                        <div class="Fill-legend-title" use:i18n={"fillLevelLabel"}>Fill level</div>
                        <div class="Fill-legend-content">
                            <div class="Fill-level-text">min</div>
                            <div class="Fill-level Fill-level--low"></div>
                            <div class="Fill-level Fill-level--light"></div>
                            <div class="Fill-level Fill-level--medium"></div>
                            <div class="Fill-level Fill-level--high"></div>
                            <div class="Fill-level Fill-level--full"></div>
                            <div class="Fill-level-text">max</div>
                        </div>

                    </div>
                </div>

            </div>
        {/if}
        </div>

    </form>
    <FormNavigation/>
</div>

